// New Conversation Modal Component
// Modal for creating new conversations with team members

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  Close,
  CheckCircle,
  RadioButtonUnchecked,
  ExpandMore,
} from '@mui/icons-material';

// Services
import { createConversation, getOrCreateChildCareTeamConversation } from '../../services/messaging';
import { getChildChatAccessHint } from '../../services/messaging/participantAccess';
import { TERMINOLOGY, formatPossessiveCareTeamLabel } from '../../constants/terminology';
import LogFormShell from '../UI/LogFormShell';
import colors from '../../assets/theme/colors';
import { ROLE_DISPLAY } from '../../constants/roles';

const normalizeContact = (user) => {
  if (!user) {
    return null;
  }

  const id = user.id || user.userId || user.uid || null;
  if (!id) {
    return null;
  }

  return {
    ...user,
    id,
    userId: user.userId || id,
    name: user.name || user.displayName || user.email || id,
  };
};

const getRoleLabel = (role) => ROLE_DISPLAY[role]?.plainLabel || role?.replace(/_/g, ' ') || 'Team member';

const getInitial = (value) => value?.trim()?.charAt(0)?.toUpperCase() || '?';

const UserSelectionItem = ({
  user,
  selected,
  onToggle,
  disabled = false
}) => (
  <Box
    onClick={() => !disabled && onToggle(user.id)}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      px: 2,
      py: 2,
      borderRadius: 3,
      border: '1.5px solid',
      borderColor: selected ? '#9CD8CF' : '#E6ECE7',
      bgcolor: selected ? '#F5FCFA' : '#FFFFFF',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition: 'border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease',
      '&:hover': disabled ? undefined : {
        borderColor: '#BFE6DF',
        bgcolor: '#FAFCFB',
      },
    }}
  >
    <Avatar
      sx={{
        width: 56,
        height: 56,
        bgcolor: selected ? '#9CD8CF' : '#C8CDD4',
        color: '#FFFFFF',
        fontSize: 28,
        fontWeight: 700,
      }}
    >
      {getInitial(user.name || user.email)}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#10213A',
          lineHeight: 1.2,
        }}
      >
        {user.name || user.email || user.id}
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          fontSize: '0.875rem',
          color: '#64748B',
          lineHeight: 1.25,
          textTransform: 'none',
        }}
      >
        {getRoleLabel(user.role)}
      </Typography>
      {user.email && user.email !== user.name ? (
        <Typography
          sx={{
            mt: 0.3,
            fontSize: '0.85rem',
            color: '#8A97A8',
            lineHeight: 1.25,
            wordBreak: 'break-word',
          }}
        >
          {user.email}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ color: selected ? '#38A89D' : '#C7CED7', display: 'flex', alignItems: 'center' }}>
      {selected ? <CheckCircle sx={{ fontSize: 36 }} /> : <RadioButtonUnchecked sx={{ fontSize: 36 }} />}
    </Box>
  </Box>
);

/**
 * Selected participants chips
 */
const SelectedParticipants = ({ participants, onRemove, currentUserId }) => {
  const selectedUsers = participants.filter(p => p.id !== currentUserId);

  if (selectedUsers.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
      {selectedUsers.map((user) => (
        <Chip
          key={user.id}
          label={user.name || user.email || user.id}
          onDelete={() => onRemove(user.id)}
          avatar={
            <Avatar sx={{ bgcolor: '#9CD8CF', color: '#FFFFFF', width: 28, height: 28, fontWeight: 700 }}>
              {getInitial(user.name || user.email)}
            </Avatar>
          }
          variant="outlined"
          deleteIcon={<Close sx={{ fontSize: 18, color: '#6B7280 !important' }} />}
          sx={{
            height: 44,
            borderRadius: 999,
            borderColor: '#CBE8E1',
            bgcolor: '#F7FCFB',
            color: '#10213A',
            '& .MuiChip-label': {
              px: 1,
              fontSize: '0.95rem',
              fontWeight: 600,
            },
          }}
        />
      ))}
    </Box>
  );
};

/**
 * Main NewConversationModal component
 */
const NewConversationModal = ({
  open,
  onClose,
  onConversationCreated,
  currentUserId,
  selectedChildId = null,
  children = [],
  availableContacts = [],
  contactsByChildId = {},
}) => {
  const { t } = useTranslation('terms');
  
  // State management
  const [conversationTitle, setConversationTitle] = useState('');
  const [selectedChild, setSelectedChild] = useState(selectedChildId || '');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const availableUsers = useMemo(() => {
    const sourceUsers = selectedChild
      ? (contactsByChildId[selectedChild] || [])
      : availableContacts;

    return sourceUsers
      .map(normalizeContact)
      .filter((user) => user.id !== currentUserId)
      .filter(Boolean);
  }, [selectedChild, contactsByChildId, availableContacts, currentUserId]);
  const loadingUsers = false;

  /**
   * Filter available users based on search
   */
  const filteredUsers = availableUsers.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Handle participant toggle
   */
  const handleParticipantToggle = (userId) => {
    const user = availableUsers.find(u => u.id === userId);
    if (!user) return;

    setSelectedParticipants(prev => {
      const isSelected = prev.some(p => p.id === userId);
      if (isSelected) {
        return prev.filter(p => p.id !== userId);
      } else {
        return [...prev, user];
      }
    });
  };

  /**
   * Remove participant
   */
  const handleRemoveParticipant = (userId) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== userId));
  };

  /**
   * Generate conversation title
   */
  const generateTitle = () => {
    const child = children?.find(c => c.id === selectedChild);
    const childName = child?.name || t('profile_one');

    if (selectedParticipants.length > 0) {
      return `${childName} Care Circle`;
    }
    
    return `${childName} Conversation`;
  };

  /**
   * Handle form submission
   */
  const handleCreateConversation = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!selectedChild) {
        setError(t('please_select_profile'));
        return;
      }

      if (selectedParticipants.length === 0) {
        setError(t('please_select_participant'));
        return;
      }

      const title = conversationTitle.trim() || generateTitle();
      // Ensure current user is included and remove duplicates
      const participantIds = Array.from(new Set([currentUserId, ...selectedParticipants.map(p => p.id)]))
        .filter(id => id && typeof id === 'string'); // Ensure all IDs are valid strings
      const sortedSelectedParticipantIds = [...participantIds].sort();
      const isDefaultCareTeamSelection = (
        defaultCareTeamParticipantIds.length > 0
        && sortedSelectedParticipantIds.length === defaultCareTeamParticipantIds.length
        && sortedSelectedParticipantIds.every((id, index) => id === defaultCareTeamParticipantIds[index])
      );

      // Additional validation after filtering
      if (participantIds.length < 2) {
        setError('Unable to create conversation - invalid participant data. Please try selecting participants again.');
        return;
      }

      const result = isDefaultCareTeamSelection
        ? await getOrCreateChildCareTeamConversation({
            childId: selectedChild,
            childName: selectedChildRecord?.name,
            createdBy: currentUserId,
          })
        : await createConversation({
            participants: participantIds,
            childId: selectedChild,
            type: 'group',
            title,
            createdBy: currentUserId,
            conversationType: 'custom_group',
          });

      if (result.success) {
        const newConversation = result.conversation
          ? {
              ...result.conversation,
              id: result.conversationId || result.conversation.id,
            }
          : {
              id: result.conversationId,
              title,
              type: 'group',
              conversationType: isDefaultCareTeamSelection ? 'child_care_team' : 'custom_group',
              participants: participantIds,
              childId: selectedChild,
              createdBy: currentUserId,
              createdAt: new Date(),
              updatedAt: new Date(),
              isActive: true,
              lastMessage: null,
              unreadCounts: Object.fromEntries(participantIds.map(id => [id, 0])),
            };

        // Notify parent and close modal
        onConversationCreated?.(newConversation);
        handleClose();
      } else {
        console.error('Failed to create conversation:', result.error);
        setError(result.error || t('creating'));
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      setError(error.message || t('creating'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (loading) return; // Prevent closing during creation
    
    // Reset form
    setConversationTitle('');
    setSelectedChild(selectedChildId || '');
    setSelectedParticipants([]);
    setSearchTerm('');
    setError(null);
    
    onClose?.();
  };

  useEffect(() => {
    if (selectedChildId && !selectedChild) {
      setSelectedChild(selectedChildId);
    }
  }, [selectedChildId, selectedChild]);

  useEffect(() => {
    if (!selectedChild && children.length > 0) {
      setSelectedChild(selectedChildId || children[0].id);
    }
  }, [children, selectedChild, selectedChildId]);

  useEffect(() => {
    setSelectedParticipants((prev) => prev.filter((participant) => (
      availableUsers.some((user) => user.id === participant.id)
    )));
  }, [availableUsers]);
  const isFormValid = selectedChild && selectedParticipants.length > 0;
  const selectedChildRecord = children.find((child) => child.id === selectedChild);
  const selectedChildName = selectedChildRecord?.name || `this ${TERMINOLOGY.subjectLower}`;
  const childOptions = children || [];
  const defaultCareTeamParticipantIds = useMemo(
    () => (contactsByChildId[selectedChild] || [])
      .map(normalizeContact)
      .filter(Boolean)
      .map((user) => user.id)
      .filter(Boolean)
      .sort(),
    [contactsByChildId, selectedChild]
  );
  const headerSubtitle = selectedChildRecord
    ? formatPossessiveCareTeamLabel(selectedChildRecord.name)
    : 'Care Circle';
  const teamHint = selectedChildRecord
    ? getChildChatAccessHint(selectedChildRecord?.userRole, selectedChildName)
    : `Choose one or more members from this ${TERMINOLOGY.subjectLower}'s care circle.`;

  return (
    <LogFormShell
      open={open}
      onClose={handleClose}
      forceDrawer
      title="New chat"
      subtitle={headerSubtitle}
      titleSx={{
        fontSize: { xs: '2rem', sm: '2.25rem' },
        lineHeight: 1.05,
        letterSpacing: '-0.03em',
        color: colors.landing.deepNavy,
      }}
      subtitleSx={{
        mt: 0.8,
        fontSize: '1rem',
        color: '#7A8395',
      }}
      closeButtonSx={{
        width: 52,
        height: 52,
        border: '1px solid #E3E8EF',
        bgcolor: '#FFFFFF',
        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.04)',
      }}
      closeIconSx={{
        fontSize: 28,
        color: colors.landing.deepNavy,
      }}
      bodySx={{
        pt: 2,
        pb: 1.5,
      }}
      surfaceSx={{
        bgcolor: '#FFFFFF',
      }}
      footer={(
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              flex: 1,
              minHeight: 56,
              borderRadius: 999,
              color: '#38A89D',
              fontSize: '1.05rem',
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateConversation}
            variant="contained"
            disabled={!isFormValid || loading}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
            sx={{
              flex: 1.45,
              minHeight: 64,
              borderRadius: 3,
              bgcolor: '#2FA39A',
              color: '#FFFFFF',
              boxShadow: 'none',
              fontSize: '1.1rem',
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#278D86',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? t('creating') : 'Start chat'}
          </Button>
        </Box>
      )}
    >
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              mb: 1.2,
              fontSize: '1.05rem',
              fontWeight: 800,
              color: colors.landing.deepNavy,
            }}
          >
            Chat for
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedChild}
              onChange={(e) => setSelectedChild(e.target.value)}
              disabled={loading}
              displayEmpty
              IconComponent={ExpandMore}
              sx={{
                borderRadius: 3,
                minHeight: 78,
                bgcolor: '#FFFFFF',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E6ECE7',
                  borderWidth: '1.5px',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 2,
                  pr: 5,
                },
              }}
              renderValue={(value) => {
                const selected = childOptions.find((child) => child.id === value);
                if (!selected) {
                  return <Typography sx={{ color: '#94A3B8' }}>Select a child</Typography>;
                }

                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Avatar sx={{ width: 44, height: 44, bgcolor: '#9CD8CF', color: '#FFFFFF', fontWeight: 700 }}>
                      {getInitial(selected.name)}
                    </Avatar>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: colors.landing.deepNavy }}>
                      {selected.name}
                    </Typography>
                  </Box>
                );
              }}
            >
              {childOptions.map((child) => (
                <MenuItem key={child.id} value={child.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#9CD8CF', color: '#FFFFFF', fontWeight: 700 }}>
                      {getInitial(child.name)}
                    </Avatar>
                    <Typography sx={{ fontWeight: 600 }}>{child.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3.25 }}>
          <Typography
            sx={{
              mb: 1.2,
              fontSize: '1.05rem',
              fontWeight: 800,
              color: colors.landing.deepNavy,
            }}
          >
            To:
          </Typography>
          <Box sx={{ minHeight: selectedParticipants.length ? 0 : 10, mb: selectedParticipants.length ? 2 : 0.8 }}>
            <SelectedParticipants
              participants={selectedParticipants}
              onRemove={handleRemoveParticipant}
              currentUserId={currentUserId}
            />
          </Box>
          <TextField
            fullWidth
            placeholder="Search care circle"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                minHeight: 68,
                bgcolor: '#FFFFFF',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E6ECE7',
                borderWidth: '1.5px',
              },
              '& .MuiInputBase-input': {
                fontSize: '1rem',
                color: '#475569',
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#7A8395', fontSize: 34 }} />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>

        <Box
          sx={{
            mb: 3,
            px: 2.25,
            py: 2,
            borderRadius: 3,
            border: '1.5px solid #D8EEE8',
            bgcolor: '#F7FCFB',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.3rem',
              fontWeight: 700,
              color: '#48556A',
              lineHeight: 1.2,
            }}
          >
            Start a chat for {selectedChildName}
          </Typography>
          <Typography
            sx={{
              mt: 0.7,
              fontSize: '1rem',
              color: '#6F7C90',
              lineHeight: 1.45,
            }}
          >
            {teamHint}
          </Typography>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              mb: 1.2,
              fontSize: '1.05rem',
              fontWeight: 800,
              color: colors.landing.deepNavy,
            }}
          >
            Care circle members
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5, maxHeight: 360, overflowY: 'auto', pr: 0.25 }}>
            {loadingUsers ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  py: 4,
                  borderRadius: 3,
                  border: '1.5px solid #E6ECE7',
                }}
              >
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('loading_care_team_members')}
                  </Typography>
              </Box>
            ) : !selectedChild ? (
              <Box sx={{ px: 2, py: 3, borderRadius: 3, border: '1.5px solid #E6ECE7', textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, color: colors.landing.deepNavy }}>
                  {t('select_profile_first_title')}
                </Typography>
                <Typography sx={{ mt: 0.6, color: '#7A8395' }}>
                  {t('select_profile_first_subtitle')}
                </Typography>
              </Box>
            ) : availableUsers.length === 0 ? (
              <Box sx={{ px: 2, py: 3, borderRadius: 3, border: '1.5px solid #E6ECE7', textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, color: colors.landing.deepNavy }}>
                  {t('no_care_team_members_found')}
                </Typography>
                <Typography sx={{ mt: 0.6, color: '#7A8395' }}>
                  {t('restriction_profile_notice')}
                </Typography>
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box sx={{ px: 2, py: 3, borderRadius: 3, border: '1.5px solid #E6ECE7', textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, color: colors.landing.deepNavy }}>
                  {t('no_members_match_search')}
                </Typography>
                <Typography sx={{ mt: 0.6, color: '#7A8395' }}>
                  {t('try_different_search')}
                </Typography>
              </Box>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = selectedParticipants.some(p => p.id === user.id);
                
                return (
                  <UserSelectionItem
                    key={user.id}
                    user={user}
                    selected={isSelected}
                    onToggle={handleParticipantToggle}
                    disabled={loading}
                  />
                );
              })
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              mb: 1.2,
              fontSize: '1.05rem',
              fontWeight: 800,
              color: colors.landing.deepNavy,
            }}
          >
            Group name <Typography component="span" sx={{ fontWeight: 500, color: '#6B7280' }}>(optional)</Typography>
          </Typography>
          <TextField
            fullWidth
            value={conversationTitle}
            onChange={(e) => setConversationTitle(e.target.value)}
            placeholder={generateTitle()}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                minHeight: 68,
                bgcolor: '#FFFFFF',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E6ECE7',
                borderWidth: '1.5px',
              },
              '& .MuiInputBase-input': {
                fontSize: '1rem',
                color: '#475569',
              },
            }}
          />
          <Typography sx={{ mt: 1, fontSize: '0.92rem', color: '#7A8395' }}>
            Leave blank to use the default chat title.
          </Typography>
        </Box>
      </Box>
    </LogFormShell>
  );
};

export default NewConversationModal;
